import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule'
import JwtToken from '@/models/Auth/JwtToken';
import cookieUtils from '@/lib/cookieUtils';
import LoginResponse from '@/models/Settings/Users/LoginResponse';
import LoginPasswordRequest from '@/models/Settings/Users/LoginPasswordRequest';
import LoginOtpRequest from '@/models/Settings/Users/LoginOtpRequest';
import ForgotPasswordRequest from '@/models/Settings/Users/ForgotPasswordRequest';
import ResetPasswordRequest from '@/models/Settings/Users/ResetPasswordRequest';
import VerifyMeRequest from '@/models/Auth/Me/VerifyMeRequest';
import VerifyMeResponse from '@/models/Auth/Me/VerifyMeResponse';
import Me from '@/models/Auth/Me/Me';

export interface IAuthState {
    loading: boolean;
    loadingLogin: boolean;
    loadingDialog: boolean;
    loadingResetPassword: boolean;
    resetPasswordSuccess: boolean;
    isAuthenticationComplete: boolean;
    loginId: string;
    me: Me;
    verifyMeResponse: VerifyMeResponse;
}

@Module({ dynamic: true, store, name: 'AuthModule', namespaced: true })
class AuthStore extends VuexModule implements IAuthState {

    public loading: boolean = true;
    public loadingLogin: boolean = false;
    public loadingDialog: boolean = false;
    public loadingResetPassword: boolean = false;
    public resetPasswordSuccess: boolean = false;
    public isAuthenticationComplete: boolean = false;
    public loginId: string = '';
    public loginNextStepName: string = '';
    public me: Me = JSON.parse(localStorage.getItem('institutionService.me'));
    public verifyMeResponse: VerifyMeResponse = null;

    @Action
    async loginPassword(request: LoginPasswordRequest) {

        this.setLoadingLogin(true);

        await ajax.post('/portal-backend/auth-api/login-password', request)
        .then(response => {
            this.context.commit("persistLoginResponse", response.data);
            this.setLoadingLogin(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingLogin(false);
        });
    }

    @Action
    async loginOtp(request: LoginOtpRequest) {

        this.setLoadingLogin(true);

        await ajax.post('/portal-backend/auth-api/login-otp', request)
        .then(response => {
            this.context.commit("persistLoginResponse", response.data);
            this.setLoadingLogin(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingLogin(false);
        });
    }

    @Action
    async forgotPassword(request: ForgotPasswordRequest) {
        await ajax.post('/portal-backend/auth-api/forgot', request)
        .then(response => {
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async resetPassword(request: ResetPasswordRequest) {

        this.setLoadingResetPassword(true);

        await ajax.post('/portal-backend/auth-api/reset', request)
        .then(response => {
            this.setLoadingResetPassword(false);
            this.setResetPasswordSuccess(true);
        })
        .catch(error => {
            this.setLoadingResetPassword(false);
            this.setResetPasswordSuccess(false);
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getMe() {

        this.setLoading(true);

        await ajax.get('/portal-backend/fi-api/me')
        .then(response => {
            this.context.commit("persistMe", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async verifyMe(request: VerifyMeRequest) {
        await ajax.post('/portal-backend/fi-api/me/verify', request)
        .then(response => {
            this.context.commit("setVerifyMe", response.data);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        // Reset values in the module
        this.loading = true;
        this.loadingLogin = false;
        this.loadingDialog = false;
        this.loadingResetPassword = false;
        this.resetPasswordSuccess = false;
        this.isAuthenticationComplete = false;
        this.loginNextStepName = "";
        this.me = null;
        this.verifyMeResponse = null;
        // Also remove locally stored values
        localStorage.removeItem('institutionService.me');
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingLogin(loading: boolean) {
        this.loadingLogin = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setLoadingResetPassword(loading: boolean) {
        this.loadingResetPassword = loading;
    }

    @Mutation
    private async setResetPasswordSuccess(success: boolean) {
        this.resetPasswordSuccess = success;
    }

    @Mutation
    async persistJwtToken(response: JwtToken) {
        if (response) {
            const tokenExpireDate = new Date(new Date().getTime() + 1000 * 20000);//response.jwtToken.expireInSeconds);
            cookieUtils.setCookieValue('SaaScada.AuthToken', response.token, tokenExpireDate, cookieUtils.appPath(), undefined);
        }
        else {
            cookieUtils.deleteCookie('SaaScada.AuthToken', undefined);
        }
    }

    @Mutation
    public async persistLoginResponse(response: LoginResponse) {
        if (response.nextStepName === "") {
            const tokenExpireDate = new Date(new Date().getTime() + 1000 * 20000);//response.jwtToken.expireInSeconds);
            cookieUtils.setCookieValue('SaaScada.AuthToken', response.jwtToken.token, tokenExpireDate, cookieUtils.appPath(), undefined);
            this.isAuthenticationComplete = true;
        }
        else {
            // Must be OTP step for now
            this.loginId = response.loginId
            this.loginNextStepName = response.nextStepName;
            this.isAuthenticationComplete = false;
        }
    }

    @Mutation
    persistMe(response: Me) {
        this.me = response;
        localStorage.setItem('institutionService.me', JSON.stringify(response));
    }

    @Mutation
    private async setVerifyMe(response: VerifyMeResponse) {
        this.verifyMeResponse = response;
    }

}

export const AuthModule = getModule(AuthStore);